import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Archive, ArchiveRestore, ArrowDown, ArrowRightLeft, ArrowUp, CopyPlus, ExternalLink, FileText, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Checkbox from '../../ui/Checkbox';
import ExpandedRowPanel from '../../ui/ExpandedRowPanel';
import Grid from '../../ui/Grid';
import Icon from '../../ui/Icon';
import Input from '../../ui/Input';
import Select from '../../ui/Select';
import StageChip from '../../ui/StageChip';
import Tabs from '../../ui/Tabs';
import Tag from '../../ui/Tag';
import Textarea from '../../ui/Textarea';
import { useConfirm } from '../../ui/Confirm';
import { useToast } from '../../ui/Toast';
import { STAGES, conditionOf, stageOf } from '../../ui/stages';
import { usePageHeader } from '../../shell/pageHeader';
import { clean } from '../../shell/copy';
import BackburnerBadge from './BackburnerBadge';
import CharacterSearch from './CharacterSearch';
import { NewScriptModal, ReassignModal, SaveTemplateModal, UseTemplateModal, formatName } from './ListModals';
import { QUAL_STATUSES, IN_PRODUCTION_STAGES, listStatus, hasLetter, displayCharacters, emptyRow } from './scriptHelpers';
import { scriptListRows } from './listRows';

// The Scripts list on src/ui (DESIGN rev 2.1 §3.1: Grid at medium density, grouped by stage, longform series as trailing
// groups, Expanded-row panel; the editor is its own route-level view, ?script=<id>).
// Same calls as before: GET /api/scripts (?deleted | ?archived | ?qualStatus=Posted|Filming, ?characterPrefix; ?templates
// in the Templates view), GET /api/scripts/status-counts (clientId, writerId, search), GET /api/scripts/characters (clientId,
// writerId, view), GET /api/clients, GET /api/team-members; PATCH /api/scripts/:id { qualNotes } (inline), PATCH
// /api/scripts/:id/status (bulk), DELETE /api/scripts/:id (bulk), POST /api/scripts/bulk-archive | bulk-unarchive |
// bulk-restore | reassign-targets | reassign, POST /api/scripts (+ PATCH body) for New script, POST /:id/save-as-template,
// POST /from-template/:id, DELETE /template/:id. Same URL params: ?q ?status ?view ?client ?writer ?characterPrefix
// (?character read once) ?sortBy ?sortDir, and ?script=<id> opens the editor.
//
// The editor (ScriptEditor) is mounted beside this component. While a script is open the list keeps its state and
// renders only the dialogs the editor can open (New script, Save as template).
//   hidden              a script is open in the editor
//   onLoadChange(bool)  the list fetch state (the editor's ?script= auto-open waits for it, as before)
//   selectScript(id)    opens a script in the editor
// ref: { loadScripts, setScripts, setShowNew, setNewScriptDefaults, setSaveTplPrompt }

const STATUS_LABEL = {
  'I/P': 'In progress', 'Needs Review': 'Needs review', 'Needs Revision': 'Needs revision',
  Approved: 'Approved', Filming: 'Filming', Posted: 'Posted', Backburner: 'Backburner',
};
const BULK_STATUSES = ['Needs Review', 'Approved', 'Backburner'];
const VIEWS = ['archived', 'deleted', 'templates'];
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const shortDate = d => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const stop = e => e.stopPropagation();
const openInNewTab = id => window.open(`${window.location.origin}/scripts?script=${id}`, '_blank', 'noopener');

// Stage from the API (FIX-1 `stage`); condition from the API's stageCondition, else from the qual status. An in-production
// stage is passed as the card status, so a script being filmed or edited carries no "Ready" badge.
const rowStage = s => (STAGES.includes(s.stage) ? s.stage : stageOf({ qualStatus: s.qualStatus }));
const rowCondition = s => {
  if (s.stageCondition) return s.stageCondition;
  const st = rowStage(s);
  return conditionOf({ qualStatus: s.qualStatus, cardStatus: IN_PRODUCTION_STAGES.includes(st) ? st : undefined });
};

// Registers the page header only while the list is the visible view (the editor keeps its own header until HD-7 S2).
function PageHeaderSpec(spec) { usePageHeader(spec); return null; }

function SortHead({ label, col, sort, onSort }) {
  const active = sort.col === col;
  const next = active && sort.dir === 'asc' ? 'descending' : 'ascending';
  return (
    <button type="button" className="ui-sort" aria-label={`${label}, sort ${next}`} onClick={() => onSort(col)}>
      {label}{active && <Icon icon={sort.dir === 'desc' ? ArrowDown : ArrowUp} />}
    </button>
  );
}

const ScriptList = forwardRef(function ScriptList({ hidden, onLoadChange, selectScript }, ref) {
  const { user, hasRole } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const [scripts,      setScripts]      = useState([]);
  const [listLoad,     setListLoad]     = useState(true);
  const [listError,    setListError]    = useState(false);
  const [search,       setSearch]       = useState(() => searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [clientFilter, setClientFilter] = useState(() => searchParams.get('client') || '');
  const [writerFilter, setWriterFilter] = useState(() => searchParams.get('writer') || '');
  // Character search lives in the Characters column header. characterQuery is the text in the field (drives the
  // suggestions); characterPrefix is the debounced value sent to the server (the one filter that refetches: the list
  // never carries script bodies). `?character=` (the retired dropdown's param) is read once; only characterPrefix is written.
  const [characterQuery,   setCharacterQuery]   = useState(() => searchParams.get('characterPrefix') || searchParams.get('character') || '');
  const [characterPrefix,  setCharacterPrefix]  = useState(() => searchParams.get('characterPrefix') || searchParams.get('character') || '');
  const [characterOptions, setCharacterOptions] = useState([]);
  // One view at a time: the production line (default), Archived, Deleted (the recycle bin, Admin / Power User), Templates.
  const [view,         setView]         = useState(() => (VIEWS.includes(searchParams.get('view')) ? searchParams.get('view') : 'active'));
  const archivedView  = view === 'archived';
  const deletedView   = view === 'deleted';
  const templatesView = view === 'templates';
  // Chip counts from the server: { active, archived: { total, … }, deleted?: { total, … } } (deleted only for Admin / PU).
  const [srvCounts,    setSrvCounts]    = useState({ active: {}, archived: { total: 0 } });
  const [templates,     setTemplates]     = useState([]);
  const [templatesLoad, setTemplatesLoad] = useState(false);
  const [saveTplPrompt, setSaveTplPrompt] = useState(null); // null | { scriptId, defaultName }
  const [useTplPrompt,  setUseTplPrompt]  = useState(null); // null | { template }
  const [scriptSort,   setScriptSort]   = useState(() => {
    const fromUrl = { col: searchParams.get('sortBy') || '', dir: searchParams.get('sortDir') || 'asc' };
    if (fromUrl.col) return fromUrl;
    try {
      const s = JSON.parse(sessionStorage.getItem('scripts_sort') || 'null');
      if (s?.col) return s;
    } catch { /* no stored sort */ }
    return { col: '', dir: 'asc' };
  });
  const [clients,      setClients]      = useState([]);
  const [members,      setMembers]      = useState([]);
  const [showNew,      setShowNew]      = useState(false);
  // New script defaults: a client to prefill and (from the editor) open the created script in a new tab.
  const [newScriptDefaults, setNewScriptDefaults] = useState(null);
  const [selectedScripts, setSelectedScripts] = useState(() => new Set());
  const [bulkStatusBusy,  setBulkStatusBusy]  = useState(false);
  const [reassignData,    setReassignData]    = useState(null);
  const [reassignResult,  setReassignResult]  = useState(null);
  const [reassignBusy,    setReassignBusy]    = useState(false);
  const [openId,          setOpenId]          = useState(null);   // the row shown in the Expanded-row panel

  // Server decides the shelf (Archived / Deleted view, Posted or Filming chip); the rest refines client-side.
  const postedChip  = statusFilter === 'Posted';
  const filmingChip = statusFilter === 'Filming';
  const loadScripts = useCallback(async () => {
    setListLoad(true);
    try {
      const p = new URLSearchParams();
      if (deletedView)       p.set('deleted', 'true');
      else if (archivedView) p.set('archived', 'true');
      else if (postedChip)   p.set('qualStatus', 'Posted');
      else if (filmingChip)  p.set('qualStatus', 'Filming');
      if (characterPrefix)   p.set('characterPrefix', characterPrefix);
      const res = await api.get('/api/scripts' + (p.toString() ? `?${p}` : ''));
      setScripts(res.data);
      setListError(false);
    } catch (err) {
      console.error('Failed to load scripts:', err);
      setListError(true);
    } finally {
      setListLoad(false);
    }
  }, [archivedView, deletedView, postedChip, filmingChip, characterPrefix]);

  useEffect(() => { loadScripts(); }, [loadScripts]);

  // Debounce the typed character query into the applied prefix. A query with no letter applies no filter (the column
  // never shows such a value, so filtering by it would leave rows with no visible reason).
  useEffect(() => {
    const q = characterQuery.trim();
    const next = hasLetter(q) ? q : '';
    if (next === characterPrefix) return undefined;
    const t = setTimeout(() => setCharacterPrefix(next), 250);
    return () => clearTimeout(t);
  }, [characterQuery, characterPrefix]);

  const loadCounts = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (clientFilter) p.set('clientId', clientFilter);
      if (writerFilter) p.set('writerId', writerFilter);
      if (search)       p.set('search', search);
      const res = await api.get('/api/scripts/status-counts' + (p.toString() ? `?${p}` : ''));
      setSrvCounts(res.data || { active: {}, archived: { total: 0 } });
    } catch { /* counts are best-effort */ }
  }, [clientFilter, writerFilter, search]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Character suggestions, scoped by client / writer / view (not by search, so they stay put while typing).
  const loadCharacterOptions = useCallback(async () => {
    const p = new URLSearchParams();
    if (clientFilter) p.set('clientId', clientFilter);
    if (writerFilter) p.set('writerId', writerFilter);
    if (deletedView)        p.set('view', 'deleted');
    else if (archivedView)  p.set('view', 'archived');
    else if (templatesView) p.set('view', 'templates');
    try {
      const res = await api.get('/api/scripts/characters' + (p.toString() ? `?${p}` : ''));
      setCharacterOptions(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCharacterOptions([]);
    }
  }, [clientFilter, writerFilter, archivedView, templatesView, deletedView]);

  useEffect(() => { loadCharacterOptions(); }, [loadCharacterOptions]);

  // Filters and sort into the URL (and sessionStorage for the sort), as before.
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (search) p.set('q', search); else p.delete('q');
      if (statusFilter) p.set('status', statusFilter); else p.delete('status');
      if (deletedView) p.set('view', 'deleted');
      else if (archivedView) p.set('view', 'archived');
      else if (templatesView) p.set('view', 'templates');
      else p.delete('view');
      if (clientFilter) p.set('client', clientFilter); else p.delete('client');
      if (writerFilter) p.set('writer', writerFilter); else p.delete('writer');
      p.delete('character');
      if (characterPrefix) p.set('characterPrefix', characterPrefix); else p.delete('characterPrefix');
      if (scriptSort.col) p.set('sortBy', scriptSort.col); else p.delete('sortBy');
      if (scriptSort.dir !== 'asc') p.set('sortDir', scriptSort.dir); else p.delete('sortDir');
      return p;
    }, { replace: true });
    if (scriptSort.col) sessionStorage.setItem('scripts_sort', JSON.stringify(scriptSort));
    else sessionStorage.removeItem('scripts_sort');
  }, [scriptSort, search, statusFilter, clientFilter, writerFilter, characterPrefix, archivedView, templatesView, deletedView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data)).catch(() => {});
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const writerOptions = useMemo(() => {
    const map = {};
    scripts.forEach(s => { if (s.writerId && s.writer?.name) map[s.writerId] = s.writer.name; });
    members.forEach(m => {
      if (m.isActive !== false && m.roles?.some(r => r.role === 'Writer' || r.role === 'Power User')) map[m.id] = m.name;
    });
    return Object.entries(map).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [scripts, members]);

  // Backburner stays off the Active view's All (its chip lists it; a search finds it by title): listRows.js.
  const { rows: shownScripts, base: shelfScripts } = useMemo(
    () => scriptListRows(scripts, { view, statusFilter, clientFilter, writerFilter, search }),
    [scripts, view, statusFilter, clientFilter, writerFilter, search]);

  const displayScripts = useMemo(() => {
    const result = shownScripts;
    if (!scriptSort.col) return result;
    const { col, dir } = scriptSort;
    return [...result].sort((a, b) => {
      let av = '', bv = '';
      if      (col === 'Script Name') { av = a.scriptName || '';   bv = b.scriptName || ''; }
      else if (col === 'Project')     { av = a.client?.name || ''; bv = b.client?.name || ''; }
      else if (col === 'Writer')      { av = a.writer?.name || ''; bv = b.writer?.name || ''; }
      else if (col === 'Status')      { av = listStatus(a) || '';  bv = listStatus(b) || ''; }
      else if (col === 'Date')        { av = a.dateEntered || '';  bv = b.dateEntered || ''; }
      else if (col === 'Approved')    { av = a.approvedAt || '';   bv = b.approvedAt || ''; }
      else return 0;
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [shownScripts, scriptSort]);

  // Stage groups in ladder order, then one group per Longform series after them (D-120: a series never sits among skit
  // peers). Within a group the order above is kept.
  const { gridGroups, groupOf } = useMemo(() => {
    const present = new Set();
    const series = [];
    for (const s of displayScripts) {
      if (s.format === 'Longform') series.push(s);
      else present.add(rowStage(s));
    }
    return {
      gridGroups: [
        ...STAGES.filter(st => present.has(st)).map(st => ({ key: st, header: <StageChip stage={st} /> })),
        ...series.map(s => ({ key: `series:${s.id}`, header: <>{clean(s.scriptName)}<Tag>Longform series</Tag></> })),
      ],
      groupOf: s => (s.format === 'Longform' ? `series:${s.id}` : rowStage(s)),
    };
  }, [displayScripts]);

  const templateRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter(t => {
      if (clientFilter && t.client?.id !== clientFilter) return false;
      if (!q) return true;
      return (t.templateName || '').toLowerCase().includes(q) || (t.scriptName || '').toLowerCase().includes(q)
          || (t.client?.name || '').toLowerCase().includes(q) || (t.writer?.name || '').toLowerCase().includes(q);
    });
  }, [templates, search, clientFilter]);

  // ── Roles (as before; the server enforces each one) ───────────────────────────
  const canMassDelete        = hasRole('Admin', 'Power User');
  const canBulkStatus        = hasRole('Writer', 'Qual Reviewer', 'Admin', 'Power User');
  const canReassign          = hasRole('Producer', 'Admin', 'Power User');
  const canSelectRows        = canMassDelete || canBulkStatus || canReassign;
  const canSeeQualNotesList  = user?.roles?.some(r => ['Admin', 'Power User', 'Qual Reviewer', 'Writer'].includes(r));
  // Read-only in the bin: PATCH /api/scripts/:id 404s while a script is deleted.
  const canEditQualNotesList = user?.roles?.some(r => ['Admin', 'Power User', 'Qual Reviewer'].includes(r)) && !deletedView;
  const canUseTemplates      = !user?.isExternal && hasRole('Writer', 'Qual Reviewer', 'Power User', 'Admin');
  const canDeleteTemplates   = !user?.isExternal && hasRole('Admin', 'Power User');

  // ── Bulk actions ────────────────────────────────────────────────────────────

  const clearSelection = () => setSelectedScripts(new Set());
  const toggleSelected = id => setSelectedScripts(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // The server applies each script's transition rules, so a mixed selection can partly fail; the toast says how much.
  const handleBulkStatus = async status => {
    const ids = [...selectedScripts];
    if (ids.length === 0) return;
    const label = STATUS_LABEL[status].toLowerCase();
    // Approved → Needs Review deletes the scripts' production cards and shoot days (server-side), so it is confirmed.
    if (status === 'Needs Review') {
      const approved = scripts.filter(s => selectedScripts.has(s.id) && s.qualStatus === 'Approved');
      if (approved.length > 0) {
        const ok = await confirm({
          title: `Move ${plural(approved.length, 'approved script')} back to needs review?`,
          body: `${approved.length} of the ${ids.length} selected ${ids.length === 1 ? 'script is' : 'scripts are'} approved. Moving ${approved.length === 1 ? 'it' : 'them'} back permanently deletes ${approved.length === 1 ? 'its' : 'their'} production cards and any scheduled shoot days. A Longform series loses its episode cards; approving again recreates them from the script.`,
          confirmLabel: 'Move back to needs review',
          destructive: true,
        });
        if (!ok) return;
      }
    }
    setBulkStatusBusy(true);
    try {
      const results = await Promise.allSettled(ids.map(id => api.patch(`/api/scripts/${id}/status`, { status })));
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      await loadScripts();
      loadCounts();
      clearSelection();
      if (failed === 0) toast(`${plural(ok, 'script')} marked ${label}.`);
      else {
        const firstErr = results.find(r => r.status === 'rejected');
        const reason = clean(firstErr?.reason?.response?.data?.error) || 'not an allowed change from their current status';
        toast(`Marked ${ok} as ${label}. ${failed} skipped: ${reason}`, { tone: 'warn' });
      }
    } finally {
      setBulkStatusBusy(false);
    }
  };

  const handleMassDeleteScripts = async () => {
    const ids = [...selectedScripts];
    const ok = await confirm({
      title: `Delete ${plural(ids.length, 'script')}?`,
      body: `${ids.length === 1 ? 'It moves' : 'They move'} to Deleted, where an Admin or Power User can restore ${ids.length === 1 ? 'it' : 'them'}.`,
      confirmLabel: `Delete ${plural(ids.length, 'script')}`,
      destructive: true,
    });
    if (!ok) return;
    try {
      await Promise.all(ids.map(id => api.delete(`/api/scripts/${id}`)));
      setScripts(prev => prev.filter(s => !ids.includes(s.id)));
      clearSelection();
      loadCounts();
      toast(`${plural(ids.length, 'script')} deleted.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'One or more scripts were not deleted. The list is reloaded; try again.', { tone: 'danger' });
      loadScripts();
    }
  };

  const handleBulkArchive = async archive => {
    const ids = [...selectedScripts];
    if (ids.length === 0) return;
    setBulkStatusBusy(true);
    try {
      const res = await api.post(`/api/scripts/${archive ? 'bulk-archive' : 'bulk-unarchive'}`, { ids });
      const n = archive ? res.data.archived : res.data.unarchived;
      clearSelection();
      await Promise.all([loadScripts(), loadCounts()]);
      toast(`${plural(n, 'script')} ${archive ? 'archived' : 'unarchived'}.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || `The scripts were not ${archive ? 'archived' : 'unarchived'}. Try again.`, { tone: 'danger' });
    } finally {
      setBulkStatusBusy(false);
    }
  };

  // Restore only clears the deleted flag: a script archived when deleted goes back to the archive shelf.
  const restore = async ids => {
    if (ids.length === 0) return;
    setBulkStatusBusy(true);
    try {
      const res = await api.post('/api/scripts/bulk-restore', { ids });
      clearSelection();
      if (ids.includes(openId)) setOpenId(null);
      await Promise.all([loadScripts(), loadCounts()]);
      toast(`${plural(res.data.restored, 'script')} restored.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The scripts were not restored. Try again.', { tone: 'danger' });
    } finally {
      setBulkStatusBusy(false);
    }
  };

  // The server says which clients can receive the selection (format and filming rules); the modal shows only those.
  const openReassign = async () => {
    const ids = [...selectedScripts];
    if (ids.length === 0) return;
    setReassignBusy(true);
    setReassignResult(null);
    try {
      const res = await api.post('/api/scripts/reassign-targets', { ids });
      setReassignData(res.data);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'Could not work out which clients can take these scripts. Try again.', { tone: 'danger' });
    } finally {
      setReassignBusy(false);
    }
  };

  const handleReassign = async clientId => {
    const ids = (reassignData?.movable || []).map(m => m.id);
    if (ids.length === 0) return;
    setReassignBusy(true);
    try {
      const res = await api.post('/api/scripts/reassign', { ids, clientId });
      setReassignResult(res.data);
      clearSelection();
      await Promise.all([loadScripts(), loadCounts()]);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The scripts were not reassigned. Try again.', { tone: 'danger' });
    } finally {
      setReassignBusy(false);
    }
  };

  // ── Create, templates, qual notes ───────────────────────────────────────────

  const handleCreate = async (clientId, title, format, rowCount, csvScenes, writerId) => {
    const newTab = !!newScriptDefaults?.openInNewTab;
    // Open the tab inside the click gesture so the browser does not block it after the awaits.
    const win = newTab ? window.open('about:blank', '_blank') : null;
    try {
      const body = { clientId, title, format: format || undefined };
      if (writerId) body.writerId = writerId;
      const res = await api.post('/api/scripts', body);
      const id = res.data.id;
      // Longform (D-089) is paper-first: no empty rows are seeded (a CSV still imports).
      if (format && (format !== 'Longform' || csvScenes)) {
        const scenes = csvScenes || Array.from({ length: rowCount }, () => emptyRow(format));
        if (scenes.length > 0) await api.patch(`/api/scripts/${id}`, { scriptBody: { format, scenes } });
      }
      await loadScripts();
      setShowNew(false);
      setNewScriptDefaults(null);
      if (newTab) {
        const url = `${window.location.origin}/scripts?script=${id}`;
        if (win) win.location = url; else window.open(url, '_blank');
      } else {
        selectScript(id);
      }
    } catch (err) {
      if (win) win.close();
      toast(clean(err.response?.data?.error) || 'The script was not created. Try again.', { tone: 'danger' });
    }
  };

  const loadTemplates = useCallback(async () => {
    setTemplatesLoad(true);
    try {
      const res = await api.get('/api/scripts?templates=true');
      setTemplates(res.data || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setTemplatesLoad(false);
    }
  }, []);

  useEffect(() => { if (templatesView) loadTemplates(); }, [templatesView, loadTemplates]);

  const handleSaveAsTemplate = async (scriptId, templateName) => {
    try {
      const res = await api.post(`/api/scripts/${scriptId}/save-as-template`, { templateName });
      setSaveTplPrompt(null);
      toast(`Saved as the template ${clean(res.data.templateName)}.`);
      if (templatesView) loadTemplates();
    } catch (err) {
      setSaveTplPrompt(null);
      toast(clean(err.response?.data?.error) || 'This script was not saved as a template. Try again.', { tone: 'danger' });
    }
  };

  const handleNewFromTemplate = async (template, clientId) => {
    try {
      const res = await api.post(`/api/scripts/from-template/${template.id}`, { clientId });
      setUseTplPrompt(null);
      setView('active');
      await loadScripts();
      selectScript(res.data.id);
    } catch (err) {
      setUseTplPrompt(null);
      toast(clean(err.response?.data?.error) || 'No script was created from this template. Try again.', { tone: 'danger' });
    }
  };

  const handleDeleteTemplate = async template => {
    const ok = await confirm({
      title: 'Delete this template?',
      body: `${clean(template.templateName || template.scriptName)} is deleted. Scripts already created from it do not change.`,
      confirmLabel: 'Delete template',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/api/scripts/template/${template.id}`);
      toast('Template deleted.');
      loadTemplates();
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The template was not deleted. Try again.', { tone: 'danger' });
    }
  };

  // Inline edit (grid cell and panel): the same PATCH as the old row textarea. The row updates at once and reverts if
  // the save fails.
  const saveQualNotes = async (s, val) => {
    const before = s.qualNotes;
    setScripts(prev => prev.map(sc => (sc.id === s.id ? { ...sc, qualNotes: val } : sc)));
    try {
      await api.patch(`/api/scripts/${s.id}`, { qualNotes: val });
    } catch (err) {
      setScripts(prev => prev.map(sc => (sc.id === s.id ? { ...sc, qualNotes: before } : sc)));
      toast(clean(err.response?.data?.error) || 'The qual note did not save. Try again.', { tone: 'danger' });
    }
  };

  const openEditor = id => { setOpenId(null); selectScript(id); };

  useImperativeHandle(ref, () => ({ loadScripts, setScripts, setShowNew, setNewScriptDefaults, setSaveTplPrompt }));
  useEffect(() => { onLoadChange?.(listLoad); }, [listLoad]); // eslint-disable-line react-hooks/exhaustive-deps

  const newScriptModal = showNew && (
    <NewScriptModal clients={clients} members={members} defaultClientId={newScriptDefaults?.clientId || ''}
      onClose={() => { setShowNew(false); setNewScriptDefaults(null); }} onCreate={handleCreate} />
  );
  const saveTemplateModal = saveTplPrompt && (
    <SaveTemplateModal defaultName={saveTplPrompt.defaultName} onClose={() => setSaveTplPrompt(null)}
      onSave={name => handleSaveAsTemplate(saveTplPrompt.scriptId, name)} />
  );

  if (hidden) return <>{newScriptModal}{saveTemplateModal}</>;

  // ── Header ──────────────────────────────────────────────────────────────────

  const filtersOn = !!(search || clientFilter || writerFilter || characterQuery);
  const clearFilters = () => { setSearch(''); setClientFilter(''); setWriterFilter(''); setCharacterQuery(''); };
  const meta = templatesView
    ? (templatesLoad ? 'Loading' : plural(templateRows.length, 'template'))
    : listLoad ? 'Loading'
      : displayScripts.length !== shelfScripts.length ? `${displayScripts.length} of ${plural(shelfScripts.length, 'script')}` : plural(shelfScripts.length, 'script');
  const changeView = v => { setView(v); clearSelection(); setOpenId(null); };
  const onSort = col => setScriptSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const sortHead = (label, col) => <SortHead label={label} col={col} sort={scriptSort} onSort={onSort} />;

  // ── Grid ────────────────────────────────────────────────────────────────────

  const open = openId ? scripts.find(s => s.id === openId) : null;
  const panelOpen = !!open;
  const allShownSelected = displayScripts.length > 0 && displayScripts.every(s => selectedScripts.has(s.id));
  const columns = [
    ...(canSelectRows ? [{
      key: 'select', width: 48,
      label: <Checkbox label={<span className="ui-sr">Select every script shown</span>} checked={allShownSelected}
        onChange={e => setSelectedScripts(e.target.checked ? new Set(displayScripts.map(s => s.id)) : new Set())} />,
      render: s => (
        <span onClick={stop}>
          <Checkbox label={<span className="ui-sr">Select {clean(s.scriptName)}</span>} checked={selectedScripts.has(s.id)} onChange={() => toggleSelected(s.id)} />
        </span>
      ),
    }] : []),
    { key: 'title', label: sortHead('Title', 'Script Name'), width: 228, render: s => (
      <span className="ui-cluster ui-cluster--nowrap" title={clean(s.scriptName)}>
        <span className="ui-truncate">{clean(s.scriptName)}</span>
      </span>
    ) },
    { key: 'stage', label: 'Stage', width: 116, render: s => <StageChip stage={rowStage(s)} /> },
    { key: 'condition', label: sortHead('Condition', 'Status'), width: 132, render: s => { const c = rowCondition(s); return c ? <Badge condition={c} /> : s.qualStatus === 'Backburner' ? <BackburnerBadge /> : null; } },
    { key: 'client', label: sortHead('Client', 'Project'), width: 72, render: s => (s.client?.name
      ? <Tag title={clean(s.client.name)}>{s.client.initials || clean(s.client.name)}</Tag> : null) },
    { key: 'format', label: 'Format', width: 116, render: s => formatName(s.format) },
    { key: 'writer', label: sortHead('Writer', 'Writer'), width: 108, render: s => (s.writer?.name ? clean(s.writer.name) : <span className="ui-meta">No writer</span>) },
    { key: 'entered', label: sortHead('Entered', 'Date'), width: 72, hide: panelOpen, render: s => shortDate(s.dateEntered) },
    // In the bin this slot reads when the script was deleted (and by whom, on hover and in the panel).
    deletedView
      ? { key: 'approved', label: 'Deleted', width: 80, hide: panelOpen, render: s => (s.deletedAt
        ? <span title={s.deletedBy?.name ? `Deleted by ${clean(s.deletedBy.name)}` : undefined}>{shortDate(s.deletedAt)}</span>
        : <span className="ui-meta" title="No audit record">Unknown</span>) }
      : { key: 'approved', label: sortHead('Approved', 'Approved'), width: 80, hide: panelOpen, render: s => shortDate(s.approvedAt) },
    ...(canSeeQualNotesList ? [{
      key: 'qualNotes', label: 'Qual notes', width: 96, hide: panelOpen,
      // Editable value is raw (never through clean); the read-only cell is display copy.
      render: s => (canEditQualNotesList ? (s.qualNotes || '') : (s.qualNotes ? <span title={clean(s.qualNotes)}>{clean(s.qualNotes)}</span> : null)),
      edit: canEditQualNotesList ? {
        value: s => s.qualNotes || '',
        onCommit: saveQualNotes,
        label: s => `Qual notes for ${clean(s.scriptName)}`,
        placeholder: 'Add note',
        multiline: true,
      } : undefined,
    }] : []),
    { key: 'characters', width: 136, hide: panelOpen, fill: true,
      label: <CharacterSearch query={characterQuery} setQuery={setCharacterQuery} options={characterOptions} />,
      // Read-only: the character column belongs to the editor. TH / AI / Recipe have no characters, so the cell is empty.
      render: s => { const names = displayCharacters(s.characters).map(clean).join(', '); return names ? <span title={names}>{names}</span> : null; } },
  ];

  const rowActions = s => (deletedView ? null : (
    <>
      <Button variant="quiet" iconOnly icon={FileText} label={`Open ${clean(s.scriptName)} in the editor`} onClick={() => openEditor(s.id)} />
      <Button variant="quiet" iconOnly icon={ExternalLink} label={`Open ${clean(s.scriptName)} in a new tab`} onClick={() => openInNewTab(s.id)} />
      {canUseTemplates && (
        <Button variant="quiet" iconOnly icon={CopyPlus} label={`Save ${clean(s.scriptName)} as a template`}
          onClick={() => setSaveTplPrompt({ scriptId: s.id, defaultName: s.scriptName || '' })} />
      )}
    </>
  ));

  const emptyFor = filtersOn || statusFilter
    ? { message: 'No script matches these filters. Clear them to see every script on this shelf.', action: { label: 'Clear filters', icon: X, onClick: () => { clearFilters(); setStatusFilter(''); } } }
    : deletedView ? { message: 'Nothing is in Deleted. A script you delete lands here and can be restored.' }
      : archivedView ? { message: 'No script is archived. Select scripts on the production line and archive them to shelve them here.' }
        : { message: 'No scripts yet. Start one with New script.', action: { label: 'New script', icon: Plus, onClick: () => { setNewScriptDefaults({ clientId: clientFilter || '', openInNewTab: false }); setShowNew(true); } } };

  const templateColumns = [
    { key: 'title', label: 'Template', width: 360, render: t => clean(t.templateName || t.scriptName) },
    { key: 'format', label: 'Format', width: 120, render: t => formatName(t.format) },
    { key: 'client', label: 'Client', width: 200, render: t => (t.client?.name ? clean(t.client.name) : null) },
    { key: 'writer', label: 'Created by', width: 200, render: t => (t.writer?.name ? clean(t.writer.name) : null) },
    { key: 'created', label: 'Created', width: 120, render: t => shortDate(t.createdAt) },
  ];
  const templateActions = t => (
    <>
      <Button variant="quiet" iconOnly icon={CopyPlus} label={`New script from ${clean(t.templateName || t.scriptName)}`} onClick={() => setUseTplPrompt({ template: t })} />
      {canDeleteTemplates && (
        <Button variant="quiet" iconOnly icon={Trash2} label={`Delete the template ${clean(t.templateName || t.scriptName)}`} onClick={() => handleDeleteTemplate(t)} />
      )}
    </>
  );

  const viewTabs = [
    { id: 'active', label: 'Active' },
    { id: 'archived', label: 'Archived', count: srvCounts.archived?.total },
    ...(canUseTemplates ? [{ id: 'templates', label: 'Templates' }] : []),
    ...(canMassDelete ? [{ id: 'deleted', label: 'Deleted', count: srvCounts.deleted?.total }] : []),
  ];
  const counts = deletedView ? srvCounts.deleted : archivedView ? srvCounts.archived : srvCounts.active;
  const n = selectedScripts.size;

  return (
    <div className="ui ui-page ui-page--flush">
      <PageHeaderSpec
        title="Scripts"
        meta={meta}
        actions={(
          <>
            <Button variant="quiet" icon={RefreshCw} onClick={templatesView ? loadTemplates : loadScripts} disabled={templatesView ? templatesLoad : listLoad}>Refresh</Button>
            <Button variant="primary" icon={Plus} onClick={() => { setNewScriptDefaults({ clientId: clientFilter || '', openInNewTab: false }); setShowNew(true); }}>New script</Button>
          </>
        )}
        filters={(
          <>
            <Input aria-label="Search scripts" icon={Search} placeholder="Search titles, clients, writers" value={search} onChange={e => setSearch(e.target.value)} />
            <Select aria-label="Client" value={clientFilter} onChange={e => setClientFilter(e.target.value)}
              options={[{ value: '', label: 'All clients' }, ...clients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
            <Select aria-label="Writer" value={writerFilter} onChange={e => setWriterFilter(e.target.value)}
              options={[{ value: '', label: 'All writers' }, ...writerOptions.map(w => ({ value: w.id, label: clean(w.name) }))]} />
            {filtersOn && <Button variant="quiet" icon={X} onClick={clearFilters}>Clear filters</Button>}
          </>
        )}
      />

      <div className="ui-inset">
        <Tabs label="Scripts view" value={view} onChange={changeView} items={viewTabs} />
      </div>

      {!templatesView && (
        <div className="ui-inset ui-inset--bar ui-toolbar">
          <span className="ui-seg" role="group" aria-label="Status">
            <Button variant="quiet" aria-pressed={!statusFilter} onClick={() => setStatusFilter('')}>All</Button>
            {QUAL_STATUSES.map(st => (
              <Button key={st} variant="quiet" aria-pressed={statusFilter === st} data-testid="scripts-chip" data-value={st}
                onClick={() => setStatusFilter(prev => (prev === st ? '' : st))}>
                {STATUS_LABEL[st]}{counts?.[st] != null && <span className="ui-meta ui-num">{counts[st]}</span>}
              </Button>
            ))}
          </span>
        </div>
      )}

      {!templatesView && n > 0 && (
        <div className="ui-inset ui-inset--bar ui-toolbar" role="toolbar" aria-label="Selected scripts">
          <span className="ui-meta">{plural(n, 'script')} selected</span>
          {deletedView ? (
            canMassDelete && <Button variant="secondary" icon={ArchiveRestore} loading={bulkStatusBusy} onClick={() => restore([...selectedScripts])}>Restore {plural(n, 'script')}</Button>
          ) : (
            <>
              {canBulkStatus && (
                <>
                  <span className="ui-meta">Mark as</span>
                  {BULK_STATUSES.map(st => (
                    <Button key={st} variant="secondary" disabled={bulkStatusBusy} onClick={() => handleBulkStatus(st)}>{STATUS_LABEL[st]}</Button>
                  ))}
                </>
              )}
              {canReassign && (
                <Button variant="secondary" icon={ArrowRightLeft} disabled={bulkStatusBusy} loading={reassignBusy && !reassignData} onClick={openReassign}>Reassign</Button>
              )}
              {canMassDelete && (
                <Button variant="secondary" icon={Archive} disabled={bulkStatusBusy} onClick={() => handleBulkArchive(!archivedView)}>{archivedView ? 'Unarchive' : 'Archive'}</Button>
              )}
              {canMassDelete && <Button variant="danger" icon={Trash2} disabled={bulkStatusBusy} onClick={handleMassDeleteScripts}>Delete</Button>}
            </>
          )}
          <Button variant="quiet" icon={X} onClick={clearSelection}>Clear selection</Button>
        </div>
      )}

      {templatesView ? (
        <div className="ui-work">
          <Grid key="templates" label="Script templates" density="medium" storageKey="hf-grid:scripts-templates"
            columns={templateColumns} rows={templateRows} rowKey={t => t.id}
            onOpen={t => setUseTplPrompt({ template: t })} rowActions={templateActions}
            state={templatesLoad ? 'loading' : 'data'}
            empty={{ message: 'No templates yet. Open a script and choose Save as template in its Actions menu.' }} />
        </div>
      ) : (
        <div className="ui-work">
          <Grid key={view} label="Scripts" density="medium" storageKey="hf-grid:scripts"
            columns={columns} rows={displayScripts} rowKey={s => s.id} groups={gridGroups} groupOf={groupOf}
            selectedKey={openId} mutedRow={s => s.qualStatus === 'Backburner'}
            onOpen={s => setOpenId(s.id)} rowActions={deletedView ? undefined : rowActions}
            state={listLoad ? 'loading' : listError ? 'error' : 'data'}
            empty={emptyFor}
            error={{ title: 'The scripts did not load', message: 'Check your connection, then try again.', onRetry: loadScripts }} />
          {open && (
            <ScriptPanel key={open.id} script={open} deletedView={deletedView}
              canSeeNotes={canSeeQualNotesList} canEditNotes={canEditQualNotesList} canUseTemplates={canUseTemplates} canRestore={canMassDelete}
              busy={bulkStatusBusy} onClose={() => setOpenId(null)} onOpenEditor={() => openEditor(open.id)}
              onSaveTemplate={() => setSaveTplPrompt({ scriptId: open.id, defaultName: open.scriptName || '' })}
              onRestore={() => restore([open.id])} onSaveNotes={val => saveQualNotes(open, val)} />
          )}
        </div>
      )}

      {newScriptModal}
      {saveTemplateModal}
      {useTplPrompt && (
        <UseTemplateModal template={useTplPrompt.template} clients={clients} onClose={() => setUseTplPrompt(null)}
          onCreate={clientId => handleNewFromTemplate(useTplPrompt.template, clientId)} />
      )}
      {reassignData && (
        <ReassignModal data={reassignData} result={reassignResult} busy={reassignBusy}
          onClose={() => { setReassignData(null); setReassignResult(null); }} onPick={handleReassign} />
      )}
    </div>
  );
});

export default ScriptList;

// ── Expanded-row panel: a quick look at the row and its quick actions; the editor is the full view ─────────────────────
function ScriptPanel({ script: s, deletedView, canSeeNotes, canEditNotes, canUseTemplates, canRestore, busy, onClose, onOpenEditor, onSaveTemplate, onRestore, onSaveNotes }) {
  const [notes, setNotes] = useState(s.qualNotes || '');
  useEffect(() => { setNotes(s.qualNotes || ''); }, [s.qualNotes]);
  const condition = rowCondition(s);
  const names = displayCharacters(s.characters);
  return (
    <ExpandedRowPanel open title={clean(s.scriptName)} onClose={onClose} label="Script details"
      footer={deletedView ? (canRestore && <Button variant="primary" icon={ArchiveRestore} loading={busy} onClick={onRestore}>Restore</Button>) : (
        <>
          <Button variant="primary" icon={FileText} onClick={onOpenEditor}>Open in editor</Button>
          <Button variant="quiet" icon={ExternalLink} onClick={() => openInNewTab(s.id)}>New tab</Button>
          {canUseTemplates && <Button variant="quiet" icon={CopyPlus} onClick={onSaveTemplate}>Save as template</Button>}
        </>
      )}>
      <dl className="ui-kv">
        <dt>Stage</dt><dd><StageChip stage={rowStage(s)} /></dd>
        <dt>Condition</dt><dd>{condition ? <Badge condition={condition} /> : s.qualStatus === 'Backburner' ? <BackburnerBadge /> : 'None'}</dd>
        <dt>Client</dt><dd>{s.client?.name ? clean(s.client.name) : 'No client'}</dd>
        <dt>Format</dt><dd>{formatName(s.format) || 'None set'}</dd>
        <dt>Writer</dt><dd>{s.writer?.name ? clean(s.writer.name) : 'No writer'}</dd>
        <dt>Reviewer</dt><dd>{s.qualReviewer?.name ? clean(s.qualReviewer.name) : 'Unassigned'}</dd>
        <dt>Entered</dt><dd>{shortDate(s.dateEntered) || 'Unknown'}</dd>
        <dt>Approved</dt><dd>{shortDate(s.approvedAt) || 'Not approved'}</dd>
        {deletedView && <><dt>Deleted</dt><dd>{s.deletedAt ? `${shortDate(s.deletedAt)}${s.deletedBy?.name ? ` by ${clean(s.deletedBy.name)}` : ''}` : 'No audit record'}</dd></>}
      </dl>
      {names.length > 0 && (
        <section className="ui-section" aria-label="Characters">
          <h3 className="ui-heading">Characters</h3>
          <div className="ui-cluster">{names.map(nm => <Tag key={nm}>{clean(nm)}</Tag>)}</div>
        </section>
      )}
      {canSeeNotes && (canEditNotes ? (
        <Textarea label="Qual notes" rows={3} value={notes} placeholder="Add a note for the writer"
          onChange={e => setNotes(e.target.value)} onBlur={() => { if (notes !== (s.qualNotes || '')) onSaveNotes(notes); }} />
      ) : (
        <section className="ui-section" aria-label="Qual notes">
          <h3 className="ui-heading">Qual notes</h3>
          <p className="ui-p ui-dense ui-prewrap">{s.qualNotes ? clean(s.qualNotes) : 'None'}</p>
        </section>
      ))}
    </ExpandedRowPanel>
  );
}
